import { KeyboardEvent, useRef } from 'react';
import { useVirtual } from 'react-virtual';
import styled from 'styled-components';

import { ENTER_KEY, SPACE_KEY } from 'constants/keyCodes';
import { DoubleLine, SelectDropdownInfoText, OptionLabel } from './Selects.styles';
import { LoadingOption } from './LoadingOption';
import { BasicOption } from './BasicOption';
import { OVERSCAN_COUNT } from './constants';
import { getFlattenedOptions } from './utils';

import { OptionsListProps, SelectValueType } from './types';
import { InfiniteScrolling } from './InfiniteScrolling';

const StyledVirtualizedContainer = styled.div`
  max-height: 200px;
  overflow: auto;
`;

export const VIRTUALIZED_CONTAINER_TEST_ID = 'virtualized-container-test-id';

export function VirtualizedOptionsList<TValue extends SelectValueType = string>({
  id,
  getOptions,
  filterText,
  emptyStateText,
  categoryDetail,
  singleSelect,
  Option = BasicOption,
  EmptyState,
  toggleDropdown,
  emptyDropdownDataTestId,
  isOpen,
  canFetch = false,
  onFetch = () => undefined,
  containerRef,
  isLoading = false,
}: OptionsListProps<TValue>) {
  const parentRef = useRef<any>();

  const options = getFlattenedOptions(getOptions, categoryDetail, filterText);
  const size = canFetch ? options.length + 1 : options.length; // Adds loading indicator to list if enabled

  const rowVirtualizer = useVirtual({
    size,
    parentRef,
    overscan: OVERSCAN_COUNT,
  });

  const showEmptyState = !options.length && !canFetch && !isLoading;

  return (
    <>
      {showEmptyState &&
        (EmptyState && !filterText ? (
          <EmptyState />
        ) : (
          <SelectDropdownInfoText data-test={emptyDropdownDataTestId}>
            {emptyStateText}
          </SelectDropdownInfoText>
        ))}
      {isLoading ? (
        <LoadingOption />
      ) : (
        <StyledVirtualizedContainer ref={parentRef} data-test={VIRTUALIZED_CONTAINER_TEST_ID}>
          <div
            style={{
              height: rowVirtualizer.totalSize, // inline styles seem required for virtualized styles to work
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.virtualItems.map((virtualRow) => {
              const { index } = virtualRow;
              const style = {
                position: 'absolute' as any,
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              };

              /** Loading indicator */
              if (index === size - 1 && canFetch) {
                return (
                  <InfiniteScrolling
                    isOpen={isOpen}
                    canFetch={canFetch}
                    onFetch={onFetch}
                    containerRef={containerRef}
                    innerRef={virtualRow.measureRef}
                    style={style}
                    key="loading-indicator"
                  />
                );
              }

              const option = options?.[index] || {};
              const { categoryId = 'default' } = option;
              const selectOption = () => {
                singleSelect(categoryId, option.value);
                toggleDropdown(false);
              };

              return (
                <Option
                  {...option}
                  aria-selected={option.isSelected}
                  badgeText={option.badgeText}
                  data-test={`${id}-option`}
                  key={option.uniqueId ?? option.id}
                  onKeyDown={({ keyCode }: KeyboardEvent) => {
                    if ([ENTER_KEY, SPACE_KEY].includes(keyCode)) {
                      selectOption();
                    }
                  }}
                  id={option.uniqueId ?? option.id}
                  isSelected={option.isSelected}
                  onClick={selectOption}
                  Prefix={option.Prefix}
                  role="option"
                  Suffix={option.Suffix}
                  tabIndex={isOpen ? 0 : undefined}
                  value={option.value}
                  innerRef={virtualRow.measureRef}
                  style={style}
                >
                  <OptionLabel>{option.label}</OptionLabel>
                  {option.doubleLine && (
                    <DoubleLine data-test="select-double-line" isSelected={option.isSelected}>
                      {option.doubleLine}
                    </DoubleLine>
                  )}
                </Option>
              );
            })}
          </div>
        </StyledVirtualizedContainer>
      )}
    </>
  );
}
